import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, Loader2, ShieldCheck } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import axios from 'axios'
import { authApi } from '@/services/api'
import BrandTopBar from '@/components/layout/BrandTopBar'

export default function LoginPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { setAuth } = useAuth()
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const response = await authApi.login(email, senha)
      const { accessToken, refreshToken, usuario } = response.data
      setAuth(accessToken, refreshToken, usuario)
      navigate('/')
    } catch (err: unknown) {
      setError(axios.isAxiosError(err) ? (err.response?.data?.detail ?? t('auth.loginError')) : t('auth.loginError'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const provider = params.get('provider')
    if (code && provider) {
      ;(async () => {
        try {
          setLoading(true)
          const res = await authApi.ssoExchange(provider, code, window.location.href)
          const { accessToken, refreshToken, usuario } = res.data
          setAuth(accessToken, refreshToken, usuario)
          window.history.replaceState({}, document.title, window.location.pathname)
          navigate('/')
        } catch {
          setError(t('auth.ssoError'))
        } finally {
          setLoading(false)
        }
      })()
    }
  }, [navigate, setAuth, t])

  return (
    <div className="goldenax-surface flex min-h-screen flex-col">
      <BrandTopBar />
      <main className="flex flex-1 items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <img src="https://goldenax.us/assets/goldenax-logo.png?v=20260908-2" alt="GoldenAx" className="mx-auto mb-3 h-32 w-auto object-contain" />
          <div className="mx-auto mb-3 h-px w-16 bg-primary-500" />
          <h1 className="text-2xl font-extrabold tracking-tight text-[#15171a]">AccessControlSaaS</h1>
          <p className="mt-2 text-sm text-slate-600">{t('auth.tagline')}</p>
        </div>

        <section className="goldenax-panel p-6 md:p-8" aria-labelledby="login-title">
          <div className="mb-6 flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-primary-100 text-primary-700">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <div>
              <h2 id="login-title" className="font-extrabold text-[#15171a]">{t('auth.accessTitle')}</h2>
              <p className="text-xs text-slate-500">{t('auth.accessSubtitle')}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="mb-2 block text-sm font-semibold text-slate-700">{t('auth.email')}</label>
              <input
                id="email" type="email" autoComplete="email" value={email}
                onChange={(e) => setEmail(e.target.value)} required
                className="w-full rounded-md border border-[#d7d4ca] bg-white px-4 py-3 text-slate-900 placeholder-slate-400 transition focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                placeholder={t('auth.emailPlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="senha" className="mb-2 block text-sm font-semibold text-slate-700">{t('auth.password')}</label>
              <div className="relative">
                <input
                  id="senha" type={showPassword ? 'text' : 'password'} autoComplete="current-password"
                  value={senha} onChange={(e) => setSenha(e.target.value)} required
                  className="w-full rounded-md border border-[#d7d4ca] bg-white px-4 py-3 pr-12 text-slate-900 placeholder-slate-400 transition focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                  placeholder="••••••••"
                />
                <button
                  type="button" onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-primary-700"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {error && <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

            <button type="submit" disabled={loading} className="goldenax-primary min-h-12 w-full gap-2 text-xs">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('auth.login')}
            </button>
          </form>

          <div className="mt-6 border-t border-[#e5e3dc] pt-5">
            <p className="text-center text-sm text-slate-600">
              {t('auth.loginWith')}{' '}
              <a href="/api/auth/sso/Google" className="font-bold text-primary-700 hover:text-primary-600">Google</a>
              {' · '}
              <a href="/api/auth/sso/Microsoft" className="font-bold text-primary-700 hover:text-primary-600">Microsoft</a>
            </p>
          </div>
        </section>
      </div>
      </main>
    </div>
  )
}
